import React, { useState, useEffect } from 'react';
import api from '../../utils/axiosConfig';
import PageLoadingArea from '../../components/PageLoader/PageLoadingArea';
import usePageLoading from '../../hooks/usePageLoading';
import '../../styles/ListPage.css';

const TermsAndConditions = () => {
    const [terms, setTerms] = useState([]);
    const [newTerm, setNewTerm] = useState('');
    const [editingTerm, setEditingTerm] = useState(null);
    const [selectedTerms, setSelectedTerms] = useState([]);
    const { isLoading, withLoading } = usePageLoading(true);

    const fetchTerms = async () => {
        const response = await api.get('/termsandcondition');
        setTerms(response.data);
    };

    useEffect(() => {
        withLoading(fetchTerms).catch((error) => {
            console.error('Error fetching terms:', error);
        });
    }, [withLoading]);

    useEffect(() => {
        if (editingTerm) {
            const term = terms.find(t => t.id === editingTerm);
            if (term) setNewTerm(term.description);
        }
    }, [editingTerm, terms]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!newTerm.trim()) return;

        try {
            if (editingTerm) {
                await api.put(`/termsandcondition/${editingTerm}`, { description: newTerm });
                setEditingTerm(null);
            } else {
                await api.post('/termsandcondition', { description: newTerm });
            }
            setNewTerm('');
            await fetchTerms();
        } catch (error) {
            console.error('Error saving term:', error);
        }
    };

    const handleCancel = () => {
        setEditingTerm(null);
        setNewTerm('');
    };

    const handleDeleteSelected = async () => {
        if (!window.confirm('Delete selected terms?')) return;
        try {
            await Promise.all(selectedTerms.map(id => api.delete(`/termsandcondition/${id}`)));
            setSelectedTerms([]);
            await fetchTerms();
        } catch (error) {
            console.error('Error deleting terms:', error);
        }
    };

    const toggleTermSelection = (id) => {
        setSelectedTerms(prev =>
            prev.includes(id) ? prev.filter(termId => termId !== id) : [...prev, id]
        );
    };

    const allSelected = terms.length > 0 && selectedTerms.length === terms.length;

    return (
        <PageLoadingArea isLoading={isLoading}>
            <div className="list-page">
                <div className="list-page-header">
                    <div className="list-page-title">
                        <h1>Terms and Conditions</h1>
                        <p className="list-page-subtitle">
                            {terms.length} term{terms.length === 1 ? '' : 's'}
                        </p>
                    </div>
                    <div className="list-header-actions">
                        <div className="list-button-group">
                            {selectedTerms.length > 0 && (
                                <button type="button" onClick={handleDeleteSelected} className="list-delete-button">
                                    Delete ({selectedTerms.length})
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="list-form-row">
                    <input
                        type="text"
                        value={newTerm}
                        onChange={(e) => setNewTerm(e.target.value)}
                        placeholder="Enter new term and condition"
                        className="list-search-input"
                    />
                    <button type="submit" className="list-add-button">
                        {editingTerm ? 'Update Term' : 'Add Term'}
                    </button>
                    {editingTerm && (
                        <button type="button" onClick={handleCancel} className="list-secondary-button">
                            Cancel
                        </button>
                    )}
                </form>

                <div className="list-table-card">
                    <div className="list-table-scroll">
                        <table className="list-table">
                            <thead>
                                <tr>
                                    <th className="col-check">
                                        <input
                                            type="checkbox"
                                            checked={allSelected}
                                            onChange={(e) => {
                                                setSelectedTerms(e.target.checked ? terms.map(term => term.id) : []);
                                            }}
                                            aria-label="Select all"
                                        />
                                    </th>
                                    <th>Description</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {terms.length === 0 ? (
                                    <tr className="list-empty-row">
                                        <td colSpan="3">
                                            <div className="list-empty-state">
                                                <h3>No terms found</h3>
                                                <p>Add your first term and condition above.</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : terms.map(term => (
                                    <tr
                                        key={term.id}
                                        className={`list-row${selectedTerms.includes(term.id) ? ' is-selected' : ''}`}
                                    >
                                        <td className="col-check">
                                            <input
                                                type="checkbox"
                                                checked={selectedTerms.includes(term.id)}
                                                onChange={() => toggleTermSelection(term.id)}
                                            />
                                        </td>
                                        <td>{term.description}</td>
                                        <td>
                                            <button
                                                type="button"
                                                className="list-secondary-button"
                                                onClick={() => setEditingTerm(term.id)}
                                                disabled={editingTerm !== null}
                                            >
                                                Edit
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </PageLoadingArea>
    );
};

export default TermsAndConditions;
